'use client';

import type { ApprovalLikelihood } from '@cc-shopping/approval-odds/odds';
import { AnalyticsFeatureViewed } from '@cc-shopping/platform-analytics/AnalyticsFeatureViewed';
import { useTrackFeatureInteracted } from '@cc-shopping/platform-analytics/hooks/useTrackFeatureInteracted';
import { InfoTooltip } from '@cc-shopping/ui/composites/InfoTooltip';
import { LoadingSpinner } from '@cc-shopping/ui/primitives/LoadingSpinner';
import { MaskIcon } from '@cc-shopping/ui/primitives/MaskIcon';
import { palette, spacing } from '@cc-shopping/ui/theme';
import { Box, Button, Typography } from '@mui/material';
import { keyframes } from '@mui/material/styles';
import { type Ref, useMemo, useRef } from 'react';
import { v4 as uuid } from 'uuid';
import { useIsApprovalOddsPage } from '../analytics/useIsApprovalOddsPage';

import { Likelihood } from '../constants';
import { useApprovalLikelihoodContext } from '../context';
import { resolveActivationOdds } from './resolveActivationOdds';

const pulseAnimation = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.35; transform: scale(0.75); }
`;

const ENTRY_LABEL = 'See my approval odds';
const CHECKING_LABEL = 'Checking approval odds';

const TIER_STYLES: Record<
  ApprovalLikelihood,
  { border: string; dot: string; label: string; display: Likelihood }
> = {
  EXCELLENT_AO: {
    border: palette.nwGreens.light,
    dot: palette.nwGreens.light,
    label: palette.greens.dark,
    display: Likelihood.Excellent,
  },
  GOOD_AO: {
    border: palette.blues.light,
    dot: palette.blues.main,
    label: palette.blues.main,
    display: Likelihood.Good,
  },
  FAIR_AO: {
    border: palette.yellows.dark,
    dot: palette.yellows.dark,
    label: '#B07000',
    display: Likelihood.Fair,
  },
  POOR_AO: {
    border: '#FF7002',
    dot: '#FF7002',
    label: '#BB442D',
    display: Likelihood.Poor,
  },
};

const pillBaseSx = {
  boxSizing: 'content-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  minHeight: `calc(40px - ${spacing(1)} * 2)`,
  px: 3,
  py: 1,
  borderRadius: '999px',
  bgcolor: 'common.white',
  textTransform: 'none' as const,
  letterSpacing: '0.08px',
  whiteSpace: 'nowrap' as const,
  width: { xs: '100%', sm: 'fit-content' },
};

function EntryPill({ onClick }: { onClick: () => void }) {
  return (
    <Button
      type="button"
      variant="blue"
      onClick={onClick}
      data-testid="activation-badge-entry"
      sx={{
        ...pillBaseSx,
        width: { xs: '100%', sm: 'auto' },
        border: '2px solid',
        borderColor: 'blues.light',
        boxShadow: `0 0 0 3px ${palette.blues['lighter-2']}`,
        '&:hover': {
          boxShadow: `0 0 0 3px ${palette.blues['lighter-2']}`,
          bgcolor: 'common.white',
          borderColor: 'blues.light',
          '& .mask-icon': {
            WebkitMaskImage: 'var(--icon-unlock)',
            maskImage: 'var(--icon-unlock)',
          },
          '& .activation-badge-entry-label': {
            textDecoration: 'underline',
          },
        },
      }}
    >
      <MaskIcon glyph="lock" color={palette.blues.main} size="24px" />
      <Typography
        className="activation-badge-entry-label"
        variant="textBold"
        sx={{ color: 'blues.main', fontSize: '16px' }}
      >
        {ENTRY_LABEL}
      </Typography>
    </Button>
  );
}

function CheckingPill() {
  return (
    <Box
      data-testid="activation-badge-checking"
      aria-hidden="true"
      sx={{
        ...pillBaseSx,
        border: '2px solid',
        borderColor: 'grey.200',
        pointerEvents: 'none',
      }}
    >
      <LoadingSpinner backgroundColor="blues.lighter" foregroundColor="blues.main" size={20} />
      <Typography variant="text" sx={{ fontWeight: 500, fontSize: '16px', color: 'text.primary' }}>
        {CHECKING_LABEL}
      </Typography>
    </Box>
  );
}

function ResultBadge({
  approvalLikelihood,
  onTooltipOpen,
  ref,
}: {
  approvalLikelihood: ApprovalLikelihood;
  onTooltipOpen?: () => void;
  /** Set by AnalyticsFeatureViewed when the badge is tracked. */
  ref?: Ref<HTMLDivElement>;
}) {
  const styles = TIER_STYLES[approvalLikelihood];
  const tierLabel = styles.display;

  return (
    <Box
      ref={ref}
      data-testid="activation-badge"
      sx={{
        ...pillBaseSx,
        border: '2px solid',
        borderColor: styles.border,
        gap: 2,
      }}
    >
      <Box
        aria-hidden="true"
        sx={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          bgcolor: styles.dot,
          flexShrink: 0,
          animation: `${pulseAnimation} 1.8s ease-in-out infinite`,
        }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography
          variant="text"
          sx={{
            fontWeight: 500,
            fontSize: '16px',
            lineHeight: '24px',
            '@media (max-width: 370px)': { fontSize: '14px' },
          }}
        >
          <Box component="span" sx={{ color: 'text.primary' }}>
            Your approval odds:{' '}
          </Box>
          <Box component="span" sx={{ color: styles.label }}>
            {tierLabel}
          </Box>
        </Typography>
        <InfoTooltip
          ariaLabel="More information about your approval likelihood"
          onOpen={onTooltipOpen}
          triggerIconWidth="16px"
          triggerIconHeight="16px"
          minWidth="16px"
          minHeight="16px"
          tooltipColor={palette.grey[500]}
          // The shared trigger hardcodes an 18px button and its transparent border adds 2px more,
          // neither of which the size props reach.
          containerStyles={{
            display: 'inline-flex',
            alignItems: 'center',
            '& > span': {
              display: 'flex',
            },
            '& > button': {
              width: '16px',
              height: '16px',
              border: 0,
              // Improve click target by expanding the button area without affecting layout
              padding: '4px',
              margin: '-4px',
            },
          }}
          text={
            <Typography variant="textSmall">
              We think your odds of getting approved for this card are{' '}
              <Typography variant="textSmallBold">{tierLabel} </Typography>
              based on your credit score and other factors from your TransUnion credit report. That
              said, these odds are an estimate to help you narrow your choices, not a guarantee.
              Card issuers use different criteria than we do.
            </Typography>
          }
        />
      </Box>
    </Box>
  );
}

export function ActivationBadge({
  productId,
  itemPosition,
}: {
  productId: string;
  itemPosition?: number;
}) {
  const { approvalLikelihoods, activationPhase, requestBannerOpen } =
    useApprovalLikelihoodContext();
  const isApprovalOddsPage = useIsApprovalOddsPage();
  const trackFeatureInteracted = useTrackFeatureInteracted();
  const odds = resolveActivationOdds(approvalLikelihoods, productId);
  // One id per mount: the impression sends it, the tooltip click chains to it.
  const badgeActivityIdRef = useRef(uuid());
  const approvalLikelihood = odds?.approvalLikelihood;
  // Stable identity: AnalyticsFeatureViewed re-fires when this object changes between renders.
  const badgeAttributes = useMemo(
    () =>
      approvalLikelihood
        ? { approval_odds: approvalLikelihood, marketplace_id: productId }
        : undefined,
    [approvalLikelihood, productId],
  );

  if (activationPhase === 'checking') {
    return <CheckingPill />;
  }

  if (activationPhase === 'activated') {
    if (!odds) {
      return null;
    }

    if (!isApprovalOddsPage) {
      return <ResultBadge approvalLikelihood={odds.approvalLikelihood} />;
    }

    return (
      <AnalyticsFeatureViewed
        entityName="approval_odds_badge"
        entitySection="roundup_product_card"
        impressionPosition={itemPosition}
        activityId={badgeActivityIdRef.current}
        additionalAttributes={badgeAttributes}
      >
        <ResultBadge
          approvalLikelihood={odds.approvalLikelihood}
          onTooltipOpen={() =>
            trackFeatureInteracted({
              entityName: 'approval_odds_badge_tooltip',
              entitySection: 'roundup_product_card',
              actionType: 'click',
              parentActivityId: badgeActivityIdRef.current,
              additionalAttributes: {
                approval_odds: odds.approvalLikelihood,
                marketplace_id: productId,
              },
            })
          }
        />
      </AnalyticsFeatureViewed>
    );
  }

  return <EntryPill onClick={() => requestBannerOpen(productId)} />;
}
